import Commons from '../../../../Services/Common/Common';
import dayjs from 'dayjs';

/**
 * WOR_BASIC_02(작업환경측정 대상물질) 스키마
 * - 조회: GetWorBasic02R1 (사업장 CustCd만 사용)
 * - 응답: GetWorBasic02R2 (ChemSeq, KorNm, EngNm, CasNo, TwaVal, StelVal, Remark)
 */
export const WOR_BASIC_02_Schema = {
  fields: {
    no: { initial: '', label: 'NO' },
    chemSeq: { initial: null, label: '대상물질일련번호' },
    korNm: { initial: '', label: '대상물질' },
    engNm: { initial: '', label: '영문명' },
    casNo: { initial: '', label: 'CAS번호' },
    twaVal: { initial: '', label: 'TWA기준값' },
    stelVal: { initial: '', label: 'STEL기준값' },
    remark: { initial: '', label: '비고' },
    chaebun: { initial: '', label: '체번' },
    custCd: { initial: '', label: '사업장코드' },
    fileName: { initial: '', label: '파일명', required: 'Y' },
    date: { initial: '', label: '작성일', required: 'Y' },
    createId: { initial: '', label: '작성자' },
    mapBlob: { initial: null, label: '블롭' },
    mapFile: { initial: null, label: '파일' },
    mapFileName: { initial: '', label: '파일명(사용자가 올린 이름)' },
    saveName: { initial: '', label: '파일명(저장되는 이름)' },
    filePath: { initial: '', label: '파일 경로' },
    previewFilePath: { initial: '', label: '미리보기 주소' },
  },

  /**
   * GetWorBasic02R1 응답 행 → 그리드 행 매핑 (Pascal → camel, 공정 필드 초기화)
   */
  mapGetWorBasic02R2RowToGrid(row) {
    if (!row) return null;
    return {
      ...row,
      chemSeq: row.ChemSeq ?? row.chemSeq,
      korNm: row.KorNm ?? row.korNm ?? '',
      engNm: row.EngNm ?? row.engNm ?? '',
      casNo: row.CasNo ?? row.casNo ?? '',
      twaVal: row.TwaVal ?? row.twaVal ?? '',
      stelVal: row.StelVal ?? row.stelVal ?? '',
      remark: row.Remark ?? row.remark ?? '',
      processA1: row.processA1 ?? '',
      processA2: row.processA2 ?? '',
      processA3: row.processA3 ?? '',
      processB0: row.processB0 ?? '',
      processB1: row.processB1 ?? '',
      processRdRoom: row.processRdRoom ?? '',
      processRdLab: row.processRdLab ?? '',
      processRdTest: row.processRdTest ?? '',
      processRdPilot: row.processRdPilot ?? '',
    };
  },

  mapper: {
    /** GetWorBasic02R1 조회 파라미터 (사업장만) */
    search1: {
      CustCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || '') || '',
    },

    save1: {
      Chaebun: (row) => Commons.EncryptAES(row.chaebun || '') || '',
      CustCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || '') || '',
      Date: (row, context) => {
        if (!row.date) return '';
        const dateValue = dayjs(row.date).format('YYYY-MM-DD');
        return Commons.EncryptAES(dateValue) || '';
      },
      FileName: (row) => Commons.EncryptAES(row.fileName || '') || '',
      SaveName: (row) => Commons.EncryptAES(row.saveName || '') || '',
      FilePath: (row, context) => {
        if (!row.saveName) return '';
        const filePath = `WorkEnvironment/${context.search?.custCd}/WOR_BASIC/${row.saveName}`;
        return Commons.EncryptAES(filePath) || '';
      },
      UserId: (_, context) => context.userInfo?.userid || '',
    },

    /** DeleteWorBasic02D1 요청 (CustCd, MatSeq) - row에 custCd 포함 후 mapRowsBySchema 호출 */
    delete1: {
      CustCd: (row) => Commons.EncryptAES(row.custCd || '') || '',
      MatSeq: (row) => Number(row.matSeq ?? row.chemSeq) ?? 0,
    },
  },

  /**
   * SaveWorBasic02U1 요청 배열 생성
   * - 서버 SaveWorBasic02U1Info: CustCd, UserId, MatSeq(0=신규), ChemSeq(신규 시 필수), ExposureLimit(신규 시 선택), Remark, CheckedGongjongs
   * - 신규(newYn==='Y'): MatSeq=0, ChemSeq=화학물질일련번호, ExposureLimit=노출허용기준
   * - 기존: MatSeq=대상물질순번, ChemSeq=null
   * @param {Array} rows - 저장할 행 배열 (merged changed rows)
   * @param {Array} gongjongColumns - 공정 컬럼 정의 [{ Field, HeaderName }]
   * @param {{ custCd: string, userId: string }} context
   * @returns {Array<{ CustCd: string, UserId: string, MatSeq: number, ChemSeq?: number, ExposureLimit?: string, Remark: string, CheckedGongjongs: string[] }>}
   */
  buildSaveWorBasic02U1Requests(rows, gongjongColumns, context) {
    if (!Array.isArray(rows) || !context) return [];
    const custCd = Commons.EncryptAES(context.custCd || '') || '';
    const userId = context.userId ?? '';
    const fields = (gongjongColumns || []).map((c) => c.Field ?? c.field ?? '').filter(Boolean);

    return rows
      .filter((row) => row.chemSeq != null && Number(row.chemSeq) > 0)
      .map((row) => {
        const isNew = row.newYn === 'Y';
        const matSeq = isNew ? 0 : Number(row.chemSeq);
        const chemSeq = isNew ? Number(row.chemSeq) : null;
        const exposureLimitRaw = row.exposureLimit ?? row.twaVal ?? row.stelVal ?? '';
        const exposureLimit = isNew && exposureLimitRaw
          ? Commons.EncryptAES(exposureLimitRaw) ?? ''
          : null;
        const remark = Commons.EncryptAES(row.remark ?? '') ?? '';
        const checkedGongjongs = fields
          .filter((field) => {
            const v = row[field];
            return v === true || v === 'Y' || v === '1' || v === 'O' || v === 'o';
          })
          .map((gongjongCd) => Commons.EncryptAES(gongjongCd ?? '') ?? '');

        const req = {
          CustCd: custCd,
          UserId: userId,
          MatSeq: matSeq,
          Remark: remark,
          CheckedGongjongs: checkedGongjongs,
        };
        if (chemSeq != null) req.ChemSeq = chemSeq;
        if (exposureLimit != null) req.ExposureLimit = exposureLimit;
        return req;
      });
  },
};

export const buildSaveWorBasic02U1Requests =
  WOR_BASIC_02_Schema.buildSaveWorBasic02U1Requests;
